import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";

const formatCurrency = (value) => `Rs. ${Number(value || 0).toLocaleString("en-IN")}`;
const formatDate = (value) => {
  if (!value) return "";
  if (value instanceof Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return String(value).split("T")[0];
};

export const isTaggedRow = (row) => Boolean(row?.tagged);
export const getTaggedProjectId = (row, fallback = "-") => row?.tagged || fallback;

const getDateAtStartOfDay = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const firstValue = (row, keys, fallback = "") => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
};

const toNumber = (row, keys) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return Number(value) || 0;
    }
  }
  return 0;
};

export const allocationDataGridSx = {
  height: "100%",
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#dbeafe",
    fontWeight: 600,
    borderRight: "1px solid #bfdbfe",
  },
  "& .MuiDataGrid-columnHeader[data-field='__selectionSort'] .MuiDataGrid-iconButtonContainer": {
    visibility: "visible",
    width: "auto",
  },
  "& .MuiDataGrid-columnHeader[data-field='__selectionSort'] .MuiDataGrid-sortIcon": {
    opacity: 1,
  },
  "& .MuiDataGrid-cell": {
    borderColor: "#eceff1",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #eceff1",
  },
};

const centerColumn = (column) => ({
  headerAlign: "center",
  align: "center",
  sortable: true,
  ...column,
});

const amountColumn = (field, headerName) =>
  centerColumn({
    field,
    headerName,
    minWidth: 150,
    flex: 1,
    type: "number",
    renderCell: ({ row }) => formatCurrency(row?.[field]),
  });

const dateColumn = (field, headerName) =>
  centerColumn({
    field,
    headerName,
    minWidth: 120,
    flex: 0.9,
    type: "date",
    valueGetter: (value) => getDateAtStartOfDay(value),
    valueFormatter: (value) => formatDate(value),
  });

export const getServiceOrderColumns = () =>
  [
    { field: "owner_site", headerName: "Owner Site", minWidth: 140, flex: 0.9 },
    { field: "order_no", headerName: "Order No.", minWidth: 140, flex: 0.9 },
    dateColumn("order_date", "Order Date"),
    { field: "supplier_name", headerName: "Supplier Name", minWidth: 200, flex: 1.3 },
    amountColumn("service_amount", "Service Amount"),
    { field: "service_ledger", headerName: "Service Ledger", minWidth: 220, flex: 1.4 },
  ].map(centerColumn);

export const getApVoucherColumns = () =>
  [
    dateColumn("voucher_date", "Voucher Date"),
    { field: "voucher_number", headerName: "Voucher No.", minWidth: 140, flex: 0.9 },
    { field: "cash_bank_ledger", headerName: "Cash / Bank Ledger", minWidth: 180, flex: 1.2 },
    { field: "name", headerName: "Name", minWidth: 180, flex: 1.2 },
    { field: "narration", headerName: "Narration", minWidth: 220, flex: 1.4 },
    { field: "instrument_no", headerName: "Instrument No.", minWidth: 140, flex: 0.9 },
    amountColumn("amount", "Amount"),
    { field: "ref_site", headerName: "Ref Site", minWidth: 140, flex: 0.9 },
  ].map(centerColumn);

export const mapServiceOrderRow = (row, index = "") => {
  const orderNo = firstValue(row, ["order_no", "ORDER_NO", "so_number", "SO_NO"]);
  const serviceLedger = firstValue(row, ["service_ledger", "SERVICE_LEDGER"]);
  const serviceAmount = toNumber(row, ["service_amount", "SERVICE_AMOUNT"]);

  return {
    id: `${orderNo || "SO"}-${serviceLedger || "LEDGER"}-${serviceAmount}${index || ""}`,
    tagged: firstValue(row, ["tagged", "TAGGED", "tagged_key", "taggedKey"]),
    project_id: firstValue(row, ["project_id", "PROJECT_ID"]),
    owner_site: firstValue(row, ["owner_site", "OWNER_SITE"]),
    order_no: orderNo,
    order_date: formatDate(firstValue(row, ["order_date", "ORDER_DATE"])),
    supplier_name: firstValue(row, ["supplier_name", "SUPPLIER_NAME"]),
    service_amount: serviceAmount,
    service_ledger: serviceLedger,
  };
};

export const mapApVoucherRow = (row, index = "") => {
  const voucherNumber = firstValue(row, ["voucher_number", "VOUCHER_NO", "VOUCHER_NUMBER"]);
  const instrumentNo = firstValue(row, ["instrument_no", "INSTRUMENT_NO"]);
  const amount = toNumber(row, ["amount", "AMOUNT"]);
  const refSite = firstValue(row, ["ref_site", "REF_SITE", "REF SITE", "store_name", "SITE_NAME"]);

  return {
    id: `${voucherNumber || "AP"}-${instrumentNo || "INS"}-${amount}${index || ""}`,
    tagged: firstValue(row, ["tagged", "TAGGED", "tagged_key", "taggedKey"]),
    project_id: firstValue(row, ["project_id", "PROJECT_ID"]),
    voucher_date: formatDate(firstValue(row, ["voucher_date", "VOUCHER_DATE"])),
    voucher_number: voucherNumber,
    cash_bank_ledger: firstValue(row, ["cash_bank_ledger", "CASH_BANK_LEDGER"]),
    name: firstValue(row, ["name", "NAME"]),
    narration: firstValue(row, ["narration", "NARRATION"]),
    instrument_no: instrumentNo,
    amount,
    ref_site: refSite,
  };
};

const mapApiRowsByFn = (rows = [], mapFn, baseKeyFn) => {
  const keyCount = new Map();
  return rows.map((row) => {
    const baseKey = baseKeyFn(row);
    const count = keyCount.get(baseKey) || 0;
    keyCount.set(baseKey, count + 1);
    return mapFn(row, count === 0 ? "" : `-${count}`);
  });
};

export const mapServiceOrderRows = (rows = []) =>
  mapApiRowsByFn(
    rows,
    mapServiceOrderRow,
    (row) =>
      `${firstValue(row, ["order_no", "ORDER_NO", "so_number", "SO_NO"])}-${firstValue(row, ["service_ledger", "SERVICE_LEDGER"])}-${toNumber(row, ["service_amount", "SERVICE_AMOUNT"])}`
  );

export const mapApVoucherRows = (rows = []) =>
  mapApiRowsByFn(
    rows,
    mapApVoucherRow,
    (row) =>
      `${firstValue(row, ["voucher_number", "VOUCHER_NO", "VOUCHER_NUMBER"])}-${firstValue(row, ["instrument_no", "INSTRUMENT_NO"])}-${toNumber(row, ["amount", "AMOUNT"])}`
  );

export const getServiceOrderMatchKey = (row) =>
  `${row?.order_no || row?.ORDER_NO || row?.so_number || row?.SO_NO || ""}|${row?.service_ledger || row?.SERVICE_LEDGER || ""}|${row?.service_amount ?? row?.SERVICE_AMOUNT ?? ""}`;

export const getApVoucherMatchKey = (row) =>
  `${row?.voucher_number || row?.VOUCHER_NO || ""}|${row?.instrument_no || row?.INSTRUMENT_NO || ""}|${row?.amount ?? row?.AMOUNT ?? ""}`;

export const getServiceOrderSearchValues = (row) => [
  row.owner_site,
  row.order_no,
  formatDate(row.order_date),
  row.supplier_name,
  row.service_amount,
  row.service_ledger,
  row.tagged,
];

export const getApVoucherSearchValues = (row) => [
  formatDate(row.voucher_date),
  row.voucher_number,
  row.cash_bank_ledger,
  row.name,
  row.narration,
  row.instrument_no,
  row.amount,
  row.ref_site,
  row.tagged,
];

export const normalizeServiceOrderForPayload = (row) => ({
  owner_site: row?.owner_site || null,
  order_no: row?.order_no || null,
  order_date: row?.order_date || null,
  supplier_name: row?.supplier_name || null,
  service_amount: row?.service_amount ?? null,
  service_ledger: row?.service_ledger || null,
});

export const normalizeApVoucherForPayload = (row) => ({
  voucher_date: row?.voucher_date || null,
  voucher_number: row?.voucher_number || null,
  cash_bank_ledger: row?.cash_bank_ledger || null,
  name: row?.name || null,
  narration: row?.narration || null,
  instrument_no: row?.instrument_no || null,
  amount: row?.amount ?? null,
  ref_site: row?.ref_site || null,
});

const getSelectionSortRank = (row, mode, isSelected) => {
  const tagged = isTaggedRow(row);
  const selected = Boolean(isSelected);
  if (mode === "unselected") {
    if (!tagged && !selected) return 0;
    if (selected) return 1;
    return 2;
  }
  if (mode === "tagged") {
    if (tagged) return 0;
    if (selected) return 1;
    return 2;
  }
  if (selected) return 0;
  if (!tagged) return 1;
  return 2;
};

export const buildSelectionColumn = ({
  isSubmitted,
  isViewOnly,
  projectTag,
  selectionSortMode,
  isBudgetAvailableRef,
  visibleSelectableRowIdsRef,
  selectedRowIdSetRef,
  setSelectedRows,
}) => ({
  field: "__selectionSort",
  headerName: "",
  width: 80,
  sortable: true,
  filterable: false,
  disableColumnMenu: true,
  sortingOrder: ["asc", null],
  type: "number",
  align: "center",
  headerAlign: "center",
  valueGetter: (_value, row) => Number(row?.__selectionSort ?? 1),
  renderHeader: () => {
    const visibleIds = visibleSelectableRowIdsRef.current;
    const selectedIds = selectedRowIdSetRef.current;
    const checked = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
    const indeterminate = visibleIds.some((id) => selectedIds.has(id)) && !checked;
    const modeLabel =
      selectionSortMode === "selected"
        ? "Selected on top"
        : selectionSortMode === "unselected"
          ? "Unselected on top"
          : selectionSortMode === "tagged"
            ? "Tagged on top"
            : "Click sort to group by selection";

    return (
      <Tooltip title={modeLabel}>
        <Checkbox
          size="small"
          checked={checked}
          indeterminate={indeterminate}
          disabled={isSubmitted || !isBudgetAvailableRef.current || !visibleIds.length || isViewOnly}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => {
            event.stopPropagation();
            setSelectedRows((prev) => {
              const next = new Set(prev);
              if (event.target.checked) {
                visibleIds.forEach((id) => next.add(id));
              } else {
                visibleIds.forEach((id) => next.delete(id));
              }
              return Array.from(next);
            });
          }}
        />
      </Tooltip>
    );
  },
  renderCell: ({ row }) => {
    if (isTaggedRow(row)) {
      return (
        <Tooltip title={`Tagged with project id: ${getTaggedProjectId(row, projectTag)}`} arrow placement="top">
          <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "100%", color: "#2563eb" }}>
            <LocalOfferOutlinedIcon fontSize="small" />
          </Box>
        </Tooltip>
      );
    }

    return (
      <Checkbox
        size="small"
        checked={Boolean(row.__selected)}
        disabled={isSubmitted || !isBudgetAvailableRef.current || isViewOnly}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => {
          event.stopPropagation();
          setSelectedRows((prev) => {
            const next = new Set(prev);
            if (event.target.checked) {
              next.add(row.id);
            } else {
              next.delete(row.id);
            }
            return Array.from(next);
          });
        }}
      />
    );
  },
});

export function useDocAllocationTable({
  isSubmitted,
  savedRows,
  apiData,
  mapRows,
  getMatchKey,
  numberField,
  searchFilter,
  tableSearch,
  searchValueGetter,
}) {
  const [selectedRows, setSelectedRows] = useState([]);
  const [gridFilterModel, setGridFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);
  const [selectionSortMode, setSelectionSortMode] = useState(null);
  const [isMoreDialogOpen, setIsMoreDialogOpen] = useState(false);
  const selectedRowIdSetRef = useRef(new Set());
  const visibleSelectableRowIdsRef = useRef([]);

  const rows = useMemo(() => {
    if (isSubmitted) {
      return savedRows.map((row, index) => ({
        ...mapRows([row])[0],
        ...row,
        id: row.id || `${getMatchKey(row)}-${index}`,
      }));
    }
    return mapRows(apiData);
  }, [apiData, getMatchKey, isSubmitted, mapRows, savedRows]);

  const selectableRowIds = useMemo(
    () => new Set(rows.filter((row) => !isTaggedRow(row)).map((row) => row.id)),
    [rows]
  );

  useEffect(() => {
    if (!savedRows.length || !rows.length) return;
    if (isSubmitted) {
      const submittedIds = rows.map((row) => row.id);
      setSelectedRows((prev) => {
        if (prev.length === submittedIds.length && prev.every((id, index) => id === submittedIds[index])) {
          return prev;
        }
        return submittedIds;
      });
      return;
    }

    const savedKeyCount = new Map();
    savedRows.forEach((row) => {
      const key = getMatchKey(row);
      savedKeyCount.set(key, (savedKeyCount.get(key) || 0) + 1);
    });
    const matchedKeyCount = new Map();
    const matchedIds = [];
    rows.forEach((row) => {
      const key = getMatchKey(row);
      if (!savedKeyCount.has(key)) return;
      const matched = matchedKeyCount.get(key) || 0;
      if (matched < savedKeyCount.get(key)) {
        matchedIds.push(row.id);
        matchedKeyCount.set(key, matched + 1);
      }
    });
    const nextIds = matchedIds.filter((id) => selectableRowIds.has(id));
    setSelectedRows((prev) => {
      if (prev.length === nextIds.length && prev.every((id) => nextIds.includes(id))) {
        return prev;
      }
      return nextIds;
    });
  }, [getMatchKey, isSubmitted, rows, savedRows, selectableRowIds]);

  const selectedDocRows = useMemo(
    () => rows.filter((row) => selectedRows.includes(row.id)),
    [rows, selectedRows]
  );
  const selectedRowIdSet = useMemo(() => new Set(selectedRows), [selectedRows]);
  selectedRowIdSetRef.current = selectedRowIdSet;

  const filteredRows = useMemo(() => {
    const numberFilters = String(searchFilter || "")
      .split(",")
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);
    const searchText = String(tableSearch || "").trim().toLowerCase();
    return rows.filter((row) => {
      const docNumber = String(row?.[numberField] || "").toLowerCase();
      const matchesNumber = !numberFilters.length || numberFilters.some((filter) => docNumber.includes(filter));
      if (!matchesNumber) return false;
      if (!searchText) return true;
      const values = searchValueGetter ? searchValueGetter(row) : Object.values(row);
      return values.some((value) => String(value ?? "").toLowerCase().includes(searchText));
    });
  }, [numberField, rows, searchFilter, searchValueGetter, tableSearch]);

  const gridRows = useMemo(
    () =>
      filteredRows.map((row) => {
        const isSelected = selectedRowIdSet.has(row.id);
        return {
          ...row,
          __selected: isSelected,
          __selectionSort: getSelectionSortRank(row, selectionSortMode || "selected", isSelected),
        };
      }),
    [filteredRows, selectedRowIdSet, selectionSortMode]
  );

  const visibleSelectableRowIds = useMemo(
    () => filteredRows.filter((row) => !isTaggedRow(row)).map((row) => row.id),
    [filteredRows]
  );
  visibleSelectableRowIdsRef.current = visibleSelectableRowIds;

  const selectedNumbers = useMemo(
    () => Array.from(new Set(selectedDocRows.map((row) => row?.[numberField]).filter(Boolean))),
    [numberField, selectedDocRows]
  );

  const handleSortModelChange = (newModel) => {
    const nextSelection = newModel.find((item) => item.field === "__selectionSort");
    const prevSelection = sortModel.find((item) => item.field === "__selectionSort");
    const otherSorts = newModel.filter((item) => item.field !== "__selectionSort");

    if (otherSorts.length > 0 && !nextSelection) {
      setSelectionSortMode(null);
      setSortModel(otherSorts);
      return;
    }

    const toggledSelectionColumn =
      Boolean(nextSelection) ||
      (newModel.length === 0 && (Boolean(prevSelection) || selectionSortMode != null));

    if (toggledSelectionColumn) {
      const nextMode =
        selectionSortMode == null
          ? "selected"
          : selectionSortMode === "selected"
            ? "unselected"
            : selectionSortMode === "unselected"
              ? "tagged"
              : null;
      setSelectionSortMode(nextMode);
      setSortModel(nextMode ? [{ field: "__selectionSort", sort: "asc" }] : []);
      return;
    }

    setSortModel(newModel);
  };

  const handleRemoveNumber = (docNumber) => {
    const rowIdsToRemove = new Set(
      rows.filter((row) => row?.[numberField] === docNumber).map((row) => row.id)
    );
    setSelectedRows((prev) => prev.filter((id) => !rowIdsToRemove.has(id)));
  };

  const handleClearSelection = () => setSelectedRows([]);

  return {
    rows,
    gridRows,
    selectedRows,
    setSelectedRows,
    selectedDocRows,
    selectedNumbers,
    gridFilterModel,
    setGridFilterModel,
    sortModel,
    handleSortModelChange,
    selectionSortMode,
    isMoreDialogOpen,
    setIsMoreDialogOpen,
    handleRemoveNumber,
    handleClearSelection,
    selectedRowIdSetRef,
    visibleSelectableRowIdsRef,
  };
}

export default function BudgetAllocationTable({
  title,
  subtitle,
  searchLabel,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  onSearch,
  searchDisabled,
  tableSearch,
  onTableSearchChange,
  recordCount,
  selectedNumbers,
  selectedLabel,
  moreDialogTitle,
  onClearSelection,
  onRemoveNumber,
  isReadOnly,
  rows,
  columns,
  loading,
  filterModel,
  onFilterModelChange,
  sortModel,
  onSortModelChange,
  noRowsLabel,
  summaryItems = [],
  children,
  embedded = false,
}) {
  const [isMoreDialogOpen, setIsMoreDialogOpen] = useState(false);

  return (
    <Stack
      padding={embedded ? 0 : 2}
      boxShadow={embedded ? "none" : "0px 1px 3px rgba(0, 0, 0, 0.2)"}
      borderRadius={2}
      marginTop={embedded ? 1 : 3}
      spacing={2}
      sx={embedded ? { pt: 2, borderTop: "1px solid #e5e7eb" } : undefined}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <TextField
            size="small"
            label={searchLabel}
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
          />
          <Button
            variant="outlined"
            size="small"
            disabled={!String(searchValue || "").trim() || searchDisabled}
            onClick={onSearch}
          >
            Search
          </Button>
          <TextField
            size="small"
            label="Search"
            placeholder="Search…"
            value={tableSearch}
            onChange={(event) => onTableSearchChange(event.target.value)}
            sx={{ minWidth: 220 }}
          />
          <Chip label={`${recordCount} Records`} color="primary" variant="outlined" />
          <Button variant="outlined" size="small" disabled={!selectedNumbers.length} onClick={onClearSelection}>
            Clear Selection
          </Button>
        </Stack>
      </Stack>

      {!!selectedNumbers.length && (
        <Box sx={{ p: 1.75, borderRadius: 2, border: "1px solid #d1fae5", backgroundColor: "#f0fdf4" }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} gap={1.5} marginBottom={1.5}>
            <Box sx={{ width: "100%" }}>
              <Typography variant="body2" color="text.secondary">{selectedLabel}</Typography>
              <Typography variant="subtitle2" fontWeight={700}>{selectedNumbers.length} Selected</Typography>
            </Box>
          </Stack>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {selectedNumbers.slice(0, 4).map((docNumber) => (
              <Box
                key={docNumber}
                sx={{
                  height: "100%",
                  minWidth: 150,
                  maxWidth: 210,
                  p: 1,
                  borderRadius: 2,
                  border: "1px solid #bbf7d0",
                  backgroundColor: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 0.5,
                }}
              >
                <Typography variant="subtitle2" fontWeight={400} sx={{ wordBreak: "break-word", flex: 1 }}>
                  {docNumber}
                </Typography>
                {!isReadOnly && (
                  <Box
                    onClick={() => onRemoveNumber(docNumber)}
                    sx={{ cursor: "pointer", color: "#dc2626", fontWeight: 700, fontSize: 16, lineHeight: 1, px: 0.5 }}
                  >
                    ×
                  </Box>
                )}
              </Box>
            ))}
            {selectedNumbers.length > 4 && (
              <Box
                onClick={() => setIsMoreDialogOpen(true)}
                sx={{
                  minWidth: 120,
                  p: 1,
                  borderRadius: 2,
                  border: "1px dashed #86efac",
                  backgroundColor: "#dcfce7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Typography variant="subtitle2" fontWeight={400} sx={{ color: "#166534" }}>
                  +{selectedNumbers.length - 4} more
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      )}

      <Box
        sx={{
          width: "100%",
          minHeight: summaryItems.length ? 220 : 180,
          maxHeight: summaryItems.length ? 408 : 360,
          height: Math.min(
            summaryItems.length ? 408 : 360,
            Math.max(summaryItems.length ? 220 : 180, (summaryItems.length ? 160 : 108) + Math.max(rows?.length || 0, 1) * 52)
          ),
          border: "1px solid #e5e7eb",
          borderRadius: 1.5,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#fff",
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            filterModel={filterModel}
            onFilterModelChange={onFilterModelChange}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            loading={loading}
            checkboxSelection={false}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            rowHeight={52}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            localeText={{ noRowsLabel }}
            sx={{
              ...allocationDataGridSx,
              border: "none",
              height: "100%",
              "& .MuiDataGrid-virtualScroller": {
                minHeight: 52,
              },
            }}
          />
        </Box>

        {!!summaryItems.length && (
          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            sx={{
              flexShrink: 0,
              px: 1.5,
              py: 1,
              borderTop: "1px solid #eceff1",
              backgroundColor: "#f8fbff",
            }}
          >
            {summaryItems.map((item) => (
              <Box key={item.label} sx={{ textAlign: "left", minWidth: item.minWidth || 110 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {item.label}
                </Typography>
                <Typography variant="body2" fontWeight={700} color={item.color || "inherit"}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      <Dialog open={isMoreDialogOpen} onClose={() => setIsMoreDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{moreDialogTitle}</DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {selectedNumbers.map((docNumber) => (
              <Box
                key={docNumber}
                sx={{
                  minWidth: 150,
                  maxWidth: 220,
                  p: 1,
                  borderRadius: 2,
                  border: "1px solid #bbf7d0",
                  backgroundColor: "#f0fdf4",
                  boxShadow: "0 6px 18px rgba(22, 101, 52, 0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 0.5,
                }}
              >
                <Typography variant="subtitle2" fontWeight={500} sx={{ wordBreak: "break-word", flex: 1 }}>
                  {docNumber}
                </Typography>
                {!isReadOnly && (
                  <Box
                    onClick={() => onRemoveNumber(docNumber)}
                    sx={{ cursor: "pointer", color: "#dc2626", fontWeight: 700, fontSize: 16, lineHeight: 1, px: 0.5 }}
                  >
                    ×
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>

      {children}
    </Stack>
  );
}
